import axios, {
  AxiosHeaders,
  CanceledError,
  HttpStatusCode
} from "axios";
import Session from "users/__session/session";
import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import Guid from "_shared/guids/guid";
import FeeSchedule from "marketplace/teams/fee-schedules/fee-schedule";
import FeeScheduleCommand from "marketplace/teams/fee-schedules/_api/commands/fee-schedule-command";
import UpdateFeeScheduleCommand from "marketplace/teams/fee-schedules/_api/commands/update-fee-schedule-command";

export default class FeeScheduleAPIService {

  private readonly headers: AxiosHeaders = new AxiosHeaders();

  constructor(session: Readonly<Session>) {
    if (!session.authToken) throw new Error("Cannot create FeeScheduleAPIService without session.");
    this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async getFeeSchedulesForTeam(teamId: Guid, abortController?: AbortController): Promise<FeeSchedule[]> {
    const url = new URL(`/marketplace/teams/${teamId.value}/fee-schedules`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(
        url.toString(), {
        headers: this.headers,
        signal: abortController?.signal
      });
      return FeeSchedule.fromJSONArray(response.data);
    }
    catch (error) {
      if (error instanceof CanceledError) 
        throw error;
      if (!axios.isAxiosError(error)) {
        throw new FeeScheduleServiceError("getFeeSchedulesForTeam", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.Forbidden: throw new FeeScheduleHiddenTeamError("User does not have access to the requested vendor-team");
        case HttpStatusCode.NotFound: throw new FeeScheduleTeamNotFoundError("Fee schedule vendor-team was not found");
        default: throw new FeeScheduleServiceError("getFeeSchedulesForTeam", error);
      }
    }
  }

  async createFeeSchedule(teamId: Guid, feeSchedule: FeeSchedule): Promise<FeeSchedule> {
    const url = new URL(`/marketplace/teams/${teamId.value}/fee-schedules`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        new FeeScheduleCommand(feeSchedule),
        {
          headers: this.headers.concat({ 'Content-Type': 'multipart/form-data' }),
        }
      );
      return FeeSchedule.fromJSON(response.data);
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new FeeScheduleServiceError("createFeeSchedule", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest: throw new FeeScheduleServiceError("createFeeSchedule", `Unable to create fee schedule: ${error.response?.data}`);
        case HttpStatusCode.Forbidden: throw new FeeScheduleServiceError("createFeeSchedule", "User does not have access to the requested vendor-team");
        case HttpStatusCode.NotFound: throw new FeeScheduleTeamNotFoundError("Fee schedule category was not found");
        default: throw new FeeScheduleServiceError("createFeeSchedule", error);
      }
    }
  }

  async updateFeeSchedule(teamId: Guid, originalFeeSchedule: FeeSchedule, updatedFeeSchedule: FeeSchedule): Promise<FeeSchedule> {
    const url = new URL(`/marketplace/teams/${teamId.value}/fee-schedules/${originalFeeSchedule.id}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.patch(
        url.toString(),
        new UpdateFeeScheduleCommand(originalFeeSchedule, updatedFeeSchedule).payload,
        {
          headers: this.headers.concat({ 'Content-Type': 'multipart/form-data' }),
        }
      );
      return FeeSchedule.fromJSON(response.data);
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new FeeScheduleServiceError("updateFeeSchedule", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest: throw new FeeScheduleServiceError("updateFeeSchedule", `Unable to update fee schedule: ${error.response.data}`);
        case HttpStatusCode.Forbidden: throw new FeeScheduleServiceError("updateFeeSchedule", "User does not have access to the requested vendor-team");
        case HttpStatusCode.NotFound: throw new FeeScheduleTeamNotFoundError("Fee schedule was not found");
        default: throw new FeeScheduleServiceError("updateFeeSchedule", error);
      }
    }
  }

  async deleteFeeSchedule(teamId: Guid, scheduleId: Guid): Promise<void> {
    const url = new URL(`/marketplace/teams/${teamId.value}/fee-schedules/${scheduleId}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(
        url.toString(),
        {
          headers: this.headers,
        }
      );
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new FeeScheduleServiceError("deleteFeeSchedule", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.Forbidden: throw new FeeScheduleServiceError("deleteFeeSchedule", "User does not have access to the requested vendor-team");
        case HttpStatusCode.NotFound: throw new FeeScheduleTeamNotFoundError("Fee schedule was not found");
        default: throw new FeeScheduleServiceError("deleteFeeSchedule", error);
      }
    }
  }
}

export class FeeScheduleServiceError extends Error {
  method: string;
  error: unknown;
  constructor(method: string, error: unknown) {
    super(`Error in FeeScheduleAPIService.${method}: ${error instanceof Error ? error.message : String(error)}`);
    this.method = method;
    this.error = error;
  }
}

export class FeeScheduleHiddenTeamError extends Error { }
export class FeeScheduleTeamNotFoundError extends Error { }
